import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Tabs,
  Spin
} from 'antd';
import { 
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { StudentBasicInfo, StudentLearningHistory, StudentTrophyBadge } from '@/pages/admin/components';
import { academyStudentApi } from '@/services/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface StudentDetail {
  id: string;
  studentName: string;
  studentId: string;
  academyCode: string;
  academyName: string;
  birthDate: string;
  parentEmail: string;
  className: string;
  teacherName: string;
  classRoomId?: number;
  assignedTeacherId?: number;
  // 가입 상태: 승인대기, 승인, 반려, 재신청
  registrationStatus: 'pending' | 'approved' | 'rejected' | 'reapplied';
  // 학생 상태: 정상, 퇴원
  studentStatus: 'normal' | 'withdrawn';
  applicationDate: string;
  approvalDate: string;
  withdrawnDate?: string;
}

const StudentDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { studentId } = useParams<{ studentId: string }>();
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState<StudentDetail | null>(null);
  const fromPath = (location.state as { from?: string } | null)?.from;
  
  // URL에서 tab 파라미터 읽기
  const urlParams = new URLSearchParams(window.location.search);
  const activeTab = urlParams.get('tab') || 'basic';

  useEffect(() => {
    const fetchDetail = async () => {
      if (!studentId) return;
      setLoading(true);
      try {
        const res: any = await academyStudentApi.detail(studentId);
        const dto = res?.data || res;
        const mapped: StudentDetail = {
          id: String(dto.academyStudentId ?? dto.userId ?? studentId),
          studentName: dto.name || '-',
          studentId: dto.studentLoginId || String(dto.academyStudentId ?? studentId),
          academyCode: dto.academyCode || '',
          academyName: dto.academyName || '',
          birthDate: dto.birthDate ? String(dto.birthDate) : '',
          parentEmail: dto.parentEmail || '',
          className: dto.className || '',
          teacherName: '',
          classRoomId: dto.classRoomId ?? undefined,
          assignedTeacherId: dto.assignedTeacherId ?? undefined,
          registrationStatus: 'approved',
          studentStatus: dto.status === '퇴원' ? 'withdrawn' : 'normal',
          applicationDate: dto.registrationDate ? String(dto.registrationDate).replace('T', ' ').slice(0, 10) : '',
          approvalDate: dto.academyRegistrationDate ? String(dto.academyRegistrationDate) : '',
          withdrawnDate: dto.withdrawnAt ? String(dto.withdrawnAt).replace('T', ' ').slice(0, 10) : '',
        };
        setStudentData(mapped);
      } catch (e) {
        setStudentData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [studentId]);

  return (
    <AdminLayout>
      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="로딩 중..." />
        </div>
      ) : !studentData ? (
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>학생 정보를 찾을 수 없습니다.</div>
        </div>
      ) : (
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        
          <Space direction="vertical" size="small" style={{ width: '100%',  marginBottom: '24px'}}>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate(fromPath || '/admin/student')}
              >
                목록으로
              </Button>
            </Space>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                {studentData.studentName} ({studentData.studentId})
              </Title>
              {(() => {
                const parts = [studentData.academyCode, studentData.className].filter(v => !!v && v !== '-');
                return parts.length ? (
                  <Text type="secondary">{parts.join(' | ')}</Text>
                ) : null;
              })()}
            </div>
          </Space>
        

        {/* 탭 영역 */}
        <Tabs defaultActiveKey={activeTab}>
          <TabPane tab="기본정보" key="basic">
            <StudentBasicInfo studentData={studentData} />
          </TabPane>
          <TabPane tab="학습이력" key="learning">
            <StudentLearningHistory studentId={String(studentId)} />
          </TabPane>
          <TabPane tab="트로피 및 뱃지" key="trophy">
            <StudentTrophyBadge studentId={String(studentId)} />
          </TabPane>
        </Tabs>
      </div>
      )}
    </AdminLayout>
  );
};

export default StudentDetail; 